<template>
  <div v-if="showLogin" class="login-overlay">
    <div class="login-modal">
      <h2>Login to Jakarto</h2>
      <p v-if="authStatus" :class="authStatus.type">{{ authStatus.message }}</p>
      
      <form @submit.prevent="handleLogin">
        <div class="form-group">
          <label for="email">Email:</label>
          <input
            type="email"
            id="email"
            v-model="credentials.email"
            required
            :disabled="isLoading"
          />
        </div>
        
        <div class="form-group">
          <label for="password">Password:</label>
          <input
            type="password"
            id="password"
            v-model="credentials.password"
            required
            :disabled="isLoading"
          />
        </div>
        
        <button type="submit" :disabled="isLoading">
          {{ isLoading ? 'Logging in...' : 'Login' }}
        </button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const emit = defineEmits(['authenticated', 'logout']);

const JAKARTO_LOGIN_URL = 'https://account.jakarto.com/users/login';
const JAKARTO_AUTH_CHECK = 'https://account.jakarto.com/auth';

const showLogin = ref(false);
const isLoading = ref(false);
const authStatus = ref(null);
const credentials = ref({
  email: '',
  password: ''
});

async function checkAuthStatus() {
  console.log('Check authentication status')
  try {
    const authResponse = await fetch(JAKARTO_AUTH_CHECK, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      mode: 'cors',
    });

    if (authResponse.ok) {
      showLogin.value = false;
      emit('authenticated', true);
      return true;
    } else {
      showLogin.value = true;
      return false;
    }
  } catch (error) {
    console.error('Authentication check failed:', error);
    showLogin.value = true;
    return false;
  }
}

async function handleLogin() {
  isLoading.value = true;
  authStatus.value = null;

  try {
    const response = await fetch(JAKARTO_LOGIN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: credentials.value.email,
        password: credentials.value.password
      }),
      credentials: 'include'
    });

    if (response.ok) {
      authStatus.value = {
        message: 'Login successful!',
        type: 'success'
      };
      credentials.value.email = '';
      credentials.value.password = '';
      
      // Recheck auth status and close modal
      await checkAuthStatus();
    } else {
      authStatus.value = {
        message: 'Login failed. Please check your credentials.',
        type: 'error'
      };
    }
  } catch (error) {
    authStatus.value = {
      message: `An error occurred: ${error.message}`,
      type: 'error'
    };
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  checkAuthStatus();
});

defineExpose({
  checkAuthStatus
});
</script>

<style scoped>
.login-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.login-modal {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 400px;
  width: 90%;
}

.login-modal h2 {
  margin-top: 0;
  margin-bottom: 1rem;
  color: #333;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #555;
}

.form-group input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: #4CAF50;
}

button {
  width: 100%;
  padding: 0.75rem;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

button:hover:not(:disabled) {
  background-color: #45a049;
}

button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.success {
  color: green;
  margin-bottom: 1rem;
}

.error {
  color: red;
  margin-bottom: 1rem;
}
</style>